import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { MouseEvent, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu, SearchFacet } from '@/pages/Search/SearchPage/types';
import { useAppSelector } from '@/redux/hooks';
import { getSearchFacets, getSelectedCategoryFacetValue } from '@/redux/modules/searchFacetAggregates.selectors';
import FacetTypes from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';
import { FilterAction } from '@/types/SearchFacet';
import { getBrowseDimensionKeyFromFacetType } from '@/utils/browsePath/browsePath';
import { modifyParamsRemovePage, parseQueryParams } from '@/utils/queryParams';
import { getSearchUrlWithParams, urlSafeTitle } from '@/utils/urls';

import { FacetCard } from '../FacetCard/FacetCard';

const ORIGIN_FACET_ID = 'F2';

export const OriginFilter = () => {
    const { formatMessage } = useIntl();

    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const semanticOriginParamName = getBrowseDimensionKeyFromFacetType(FacetTypes.ORIGIN_ID) ?? paramTypes.CATEGORY_ID;
    const currentOriginId = queryParams[semanticOriginParamName] ?? queryParams[paramTypes.CATEGORY_ID];

    const nestedOriginFacet = useAppSelector((state) => getSelectedCategoryFacetValue(state, FacetTypes.ORIGIN_ID));
    const facets = useAppSelector(getSearchFacets);
    const navigateWithParams = useNavigateSearchPageWithParams();

    const handleNavigation = useCallback(
        (event: MouseEvent<HTMLAnchorElement | HTMLInputElement>, params: any) => {
            event.preventDefault();

            navigateWithParams({
                checkForPageRemoval: true,
                currentOpenMenu: CurrentOpenMenu.AllFilters,
                filterAction: FilterAction.OriginChanged,
                params,
                preventScrollReset: true,
            });
        },
        [navigateWithParams]
    );

    const buildOriginCategoryHref = (categoryId: string, categoryName: string, remove?: boolean) => {
        let newParams = modifyParamsRemovePage(queryParams);
        if (remove) {
            delete newParams[semanticOriginParamName];
        } else {
            newParams[semanticOriginParamName] = urlSafeTitle(categoryName || categoryId);
        }

        return newParams;
    };

    const originFacet = useMemo(() => {
        let originFacet: SearchFacet;

        facets.forEach((facet) => {
            switch (facet.id) {
                case ORIGIN_FACET_ID:
                    originFacet = facet as SearchFacet;
                    break;

                default:
                    break;
            }
        });

        return originFacet;
    }, [facets]);

    if (!originFacet) {
        return null;
    }
    const { categories } = originFacet;

    return (
        <FacetCard headline={formatMessage({ id: 'search_facet_title_F2' })}>
            {/* L1 Categories */}
            {Boolean(!nestedOriginFacet.categories) &&
                categories?.facets[0]?.id !== currentOriginId &&
                categories.facets.map((option) => (
                    <SubtleMonochromeLink
                        href={getSearchUrlWithParams(buildOriginCategoryHref(option.id, option.name))}
                        key={option.id}
                        onClick={(event) => {
                            handleNavigation(event, buildOriginCategoryHref(option.id, option.name));
                        }}
                    >
                        {option.name}
                    </SubtleMonochromeLink>
                ))}
            {/* ALL OTHER LEVELS */}
            {Boolean(nestedOriginFacet.categories) && (
                <>
                    <H5 className="text-link-primary">{nestedOriginFacet.name}</H5>
                    {nestedOriginFacet.categories.map((option) => (
                        <SubtleMonochromeLink
                            href={getSearchUrlWithParams(buildOriginCategoryHref(option.id, option.name))}
                            key={option.id}
                            onClick={(event) => {
                                handleNavigation(event, buildOriginCategoryHref(option.id, option.name));
                            }}
                        >
                            {option.name}
                        </SubtleMonochromeLink>
                    ))}
                </>
            )}
        </FacetCard>
    );
};
